import { useState } from 'react';

import { OutcomeBadge } from '@/components/badges';
import { DisclosureChevron, ExternalLink, Gap } from '@/components/icons';
import { Notice, Table, Td, Th } from '@/components/primitives';
import { Reveal } from '@/components/Reveal';
import { Timestamp } from '@/components/Timestamp';
import { formatMillis } from '@/domain/time';
import type { EvidenceRun, JudgedRun } from '@/domain/types';

/**
 * The evidence snapshot on a proposal (TDD §4.5): one line per run in the streak, expandable
 * to the run's checks when the run still exists (runs expire after 90 days; the snapshot
 * does not).
 */
export function EvidenceList({
  evidence,
  runs,
  hadGap,
  checksOrder,
}: {
  evidence: EvidenceRun[];
  runs?: JudgedRun[] | undefined;
  hadGap: boolean;
  checksOrder?: string[] | undefined;
}) {
  const byKey = new Map((runs ?? []).map((r) => [r.run_key, r]));
  if (evidence.length === 0) {
    return <p className="text-body text-ink-3">No evidence runs (made by hand).</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {hadGap ? (
        <Notice tone="warning" className="flex items-start gap-2">
          <Gap size={14} className="shrink-0 mt-0.5" />
          <span>A blind period falls inside this evidence.</span>
        </Notice>
      ) : null}
      {/* A tinted list rather than a bordered box: it already sits inside a card. */}
      <ol className="m-0 list-none divide-y divide-line overflow-hidden rounded-sm bg-surface-2 p-0">
        {evidence.map((item, index) => (
          <EvidenceItem
            key={item.run_key}
            item={item}
            run={byKey.get(item.run_key)}
            index={index}
            newest={index === 0}
            checksOrder={checksOrder}
          />
        ))}
      </ol>
    </div>
  );
}

function EvidenceItem({
  item,
  run,
  newest,
  checksOrder,
}: {
  item: EvidenceRun;
  run: JudgedRun | undefined;
  index: number;
  newest: boolean;
  checksOrder: string[] | undefined;
}) {
  const [open, setOpen] = useState(false);
  const canExpand = Boolean(run);
  return (
    <li className="text-body">
      <div className="flex items-center gap-3 px-3 py-1.5">
        {canExpand ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide checks' : 'Show checks'}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex size-6 items-center justify-center rounded-sm text-ink-2 transition-tone hover:bg-surface-3 hover:text-ink"
          >
            <DisclosureChevron open={open} size={14} />
          </button>
        ) : (
          <span className="size-6 inline-block" aria-hidden="true" />
        )}
        <OutcomeBadge outcome={item.outcome} />
        <Timestamp iso={item.completed_at} mode="auto" className="text-ink-2" />
        {item.driving_check ? (
          <span className="text-ink-2 truncate">
            <code>{item.driving_check}</code>
          </span>
        ) : null}
        {newest ? <span className="ml-auto text-meta text-ink-3">newest</span> : null}
        {!run ? (
          <span className={['text-meta text-ink-3', newest ? '' : 'ml-auto'].join(' ')}>
            run expired
          </span>
        ) : null}
      </div>
      {run ? (
        <Reveal open={open}>
          <ChecksTable run={run} checksOrder={checksOrder} />
        </Reveal>
      ) : null}
    </li>
  );
}

export function ChecksTable({
  run,
  checksOrder,
}: {
  run: JudgedRun;
  checksOrder: string[] | undefined;
}) {
  const order = new Map((checksOrder ?? []).map((name, i) => [name, i]));
  const checks = [...run.checks].sort(
    (a, b) => (order.get(a.name) ?? 99) - (order.get(b.name) ?? 99),
  );
  return (
    <div className="bg-surface border-t border-line px-3 py-2">
      <p className="text-meta text-ink-3 mb-1.5 flex flex-wrap gap-x-3">
        <span>
          Run <code>{run.run_id}</code> from <code>{run.source}</code>
        </span>
        <span>
          started <Timestamp iso={run.started_at} mode="clock" withSeconds /> · completed{' '}
          <Timestamp iso={run.completed_at} mode="clock" withSeconds />
        </span>
      </p>
      <Table className="text-meta [&>tbody>tr:last-child>td]:border-b-0">
        <thead>
          <tr>
            <Th>Check</Th>
            <Th>Outcome</Th>
            <Th>Observed</Th>
            <Th className="text-right">Duration</Th>
            <Th>Error</Th>
          </tr>
        </thead>
        <tbody>
          {checks.map((check) => (
            <tr key={check.name}>
              <Td>
                <code>{check.name}</code>
              </Td>
              <Td>
                <OutcomeBadge outcome={check.outcome} />
              </Td>
              <Td>
                <Timestamp iso={check.observed_at} mode="clock" withSeconds />
              </Td>
              <Td className="text-right tabular">
                {check.duration_ms !== undefined ? formatMillis(check.duration_ms) : '—'}
              </Td>
              <Td className="max-w-[360px]">
                {check.error_code ? (
                  <span className="break-words">
                    <code>{check.error_code}</code>
                    {check.error_message ? (
                      <span className="text-ink-2"> · {check.error_message}</span>
                    ) : null}
                    {check.artifact_url ? (
                      <>
                        {' '}
                        <a
                          href={check.artifact_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1"
                        >
                          Logs <ExternalLink size={12} />
                        </a>
                      </>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-ink-3">—</span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
